'use client';

import Box, {type BoxProps} from '@mui/material/Box';
import {styled} from '@mui/material/styles';
import NextLink from 'next/link';
import {usePathname} from 'next/navigation';
import {trackBackToHomepageClick} from '../../../analytics';
import {Button, Typography} from '../../../components/primitives';

// Centered 404 content block (Figma 3885:40409); header/footer come from the
// surrounding layout. Responsive at the `md` breakpoint.
export interface NotFoundPageProps {
  title: string;
  body: string;
  ctaLabel: string;
  ctaHref: string;
  illustrationSrc: string;
}

const Section = styled(Box)<BoxProps>(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  flex: 1,
  backgroundColor: theme.palette.colors.surface.neutral.page,
  paddingBlock: '80px',
  paddingInline: theme.spacing(2.25),
  [theme.breakpoints.up('md')]: {
    paddingBlock: '150px',
  },
}));

export default function NotFoundPage({title, body, ctaLabel, ctaHref, illustrationSrc}: NotFoundPageProps) {
  const pathname = usePathname();
  return (
    <Section component="section">
      <Box
        component="img"
        src={illustrationSrc}
        alt=""
        sx={{
          width: {xs: '175px', md: '210px'},
          height: 'auto',
          mb: {xs: 1.25, md: 2},
        }}
      />
      <Typography
        as="h1"
        size="title-2"
        weight="bold"
        mobileSize="title-6"
        mobileWeight="bold"
        sx={{textTransform: 'capitalize', color: theme => theme.palette.colors.text.neutral.strong}}
      >
        {title}
      </Typography>
      <Typography
        as="p"
        size="body-1"
        weight="regular"
        lineHeight="22"
        sx={theme => ({
          color: theme.palette.colors.text.neutral.default,
          mt: 0.75,
          maxWidth: '287px',
          [theme.breakpoints.up('md')]: {mt: 1, maxWidth: '382px'},
        })}
      >
        {body}
      </Typography>
      <Button
        component={NextLink}
        href={ctaHref}
        sx={{mt: 3}}
        onClick={() => {
          trackBackToHomepageClick(pathname, typeof document === 'undefined' ? undefined : document.referrer || undefined);
        }}
      >
        {ctaLabel}
      </Button>
    </Section>
  );
}
